import React, { useMemo, useState } from "react";
import { Card, Dialog, DialogContent, DialogTitle, Typography, styled } from "@mui/material";
import { ParentSize } from "@visx/responsive";
import { useLazyLoadQuery } from "react-relay";
import GeneralViewMfd from "./GeneralViewMfd";
import GeneralViewMfdStaticDialog from "./GeneralViewMfdStaticDialog";
import { MetaToolTip } from "../common/MetaToolTip";
import { MetaArguments, SolutionDiagnosticsOption } from "../../views/GeneralTask/generalTask.types";
import { getSeriesNames, mfdChartQuery, MfdTableMeta, parseSeries, Series } from "../common/MfdChart";
import { MfdChartQuery } from "../common/__generated__/MfdChartQuery.graphql";
import { TOTAL_MFD_CUMULATIVE } from "../../constants/diagnosticReport";

interface GeneralViewMfdDynamicDialogProps {
  id: string;
  mfdTableMeta: MfdTableMeta | undefined;
  currentImage: number;
  automationTasksLength: number;
  option: SolutionDiagnosticsOption;
  filteredMeta: MetaArguments;
}

const cumulativeSeries = (series: Series): Series => {
  const newSeries = {
    name: series.name,
    displayName: series.displayName,
    // Each point is copied, not just the array: the loop below writes to
    // `rate`, and sharing the point objects would accumulate into the
    // incremental series this was derived from.
    data: series.data.map((point) => ({ ...point })),
    color: series.color,
  };

  for (let x = newSeries.data.length - 1; x > 0; x--) {
    newSeries.data[x - 1].rate += newSeries.data[x].rate;
  }
  return newSeries;
};

const StyledChartContainer = styled("div")(() => ({
  padding: "5px",
  maxHeight: "100%",
  width: "100%",
  objectFit: "contain",
}));

const StyledCard = styled(Card)(() => ({
  padding: "0px",
  maxHeight: "100%",
  width: "33%",
  objectFit: "contain",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: "none",
  boxShadow: "none",
}));

const GeneralViewMfdDynamicDialog: React.FC<GeneralViewMfdDynamicDialogProps> = ({
  id,
  mfdTableMeta,
  option,
  currentImage,
  automationTasksLength,
  filteredMeta,
}: GeneralViewMfdDynamicDialogProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const cumulative = option.id === TOTAL_MFD_CUMULATIVE;
  const data = useLazyLoadQuery<MfdChartQuery>(mfdChartQuery, { id: mfdTableMeta?.id ?? "" });
  const rows = data?.node?.rows;

  const series = useMemo<Series[]>(() => {
    if (!mfdTableMeta || !rows?.length) {
      return [];
    }
    const seriesNames = getSeriesNames(rows, mfdTableMeta.subduction, mfdTableMeta.isV2);
    const selectedNames = mfdTableMeta.isV2 ? seriesNames.Both : seriesNames.preV2;
    const parsed = selectedNames.map((seriesName) => parseSeries(rows, seriesName));
    return cumulative ? parsed.map(cumulativeSeries) : parsed;
  }, [rows, mfdTableMeta, cumulative]);

  // Newer inversion solutions can have an MFD table that is present but empty,
  // and some tables hold no series of the type this solution expects. Either way
  // there is nothing to plot, so show the static report figure instead of an
  // empty chart.
  if (!series.length) {
    return (
      <GeneralViewMfdStaticDialog
        id={id}
        option={option}
        currentImage={currentImage}
        automationTasksLength={automationTasksLength}
        filteredMeta={filteredMeta}
      />
    );
  }

  return (
    <>
      <StyledCard>
        <StyledChartContainer onClick={handleClickOpen} key={option.finalPath}>
          <ParentSize>
            {(parent) => (
              <GeneralViewMfd
                mfdTableMeta={mfdTableMeta}
                parentWidth={parent.width}
                series={series}
                cumulative={cumulative}
              />
            )}
          </ParentSize>
        </StyledChartContainer>
      </StyledCard>
      <Dialog
        fullWidth={true}
        maxWidth={"lg"}
        open={open}
        onClick={handleClose}
        slotProps={{ paper: { sx: { height: "100%", width: "50%" } } }}
        style={{ zIndex: "unset" }}
      >
        <DialogTitle style={{ display: "flex", flexWrap: "wrap", fontSize: 16 }}>
          <Typography>
            <strong>
              {currentImage + 1}&nbsp;of&nbsp;{automationTasksLength}:
            </strong>
            &nbsp;
          </Typography>
          <MetaToolTip meta={filteredMeta} />
        </DialogTitle>
        <DialogContent>
          <StyledChartContainer>
            <ParentSize>
              {(parent) => (
                <GeneralViewMfd
                  mfdTableMeta={mfdTableMeta}
                  parentWidth={parent.width}
                  series={series}
                  cumulative={cumulative}
                />
              )}
            </ParentSize>
          </StyledChartContainer>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GeneralViewMfdDynamicDialog;
